// src/pages/AgentsPage.jsx — v2 (wireframe-aligned, light theme)
//
// Página de gerenciamento de agentes baseada no wireframe enviado pelo
// usuário. Consome o novo endpoint /users/agents/stats que devolve numa
// só chamada: conversas atendidas no mês, templates+respostas, TMA,
// ranking e online status (atividade nos últimos 5 min).
//
// Estrutura:
//   - Topbar light (inline) com breadcrumb + clock + Voltar + Novo Agente
//   - Page header com pills de status (ativo / plano · X/Y / limite)
//   - Slim notice (amber) quando limite atingido
//   - Toolbar: busca + filtros (Todos/Online/Offline) + sort
//   - Tabela 7-col: agente · função · status · conversas · TMA · ranking · ações
//   - Locked slot rows (placeholder de upgrade) quando limit do plano atingido
//   - Ranking footer (top 3) — blur+teaser pra STARTER, real pra BUSINESS+
import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  FiUsers, FiSearch, FiTrash2, FiArrowLeft, FiMail, FiUser, FiLock,
  FiEdit2, FiPlus, FiStar, FiZap, FiKey,
} from 'react-icons/fi';
import AppSidebar from '../components/AppSidebar';
import api from '../services/Api';
import usePlanUsage from '../hooks/usePlanUsage';

// ─── Paleta light v2 (mesma de Conversations.js + Chat) ─────────────────────
const C = {
  bg:        '#F0F3F8',
  card:      '#FFFFFF',
  surf:      '#F7F9FC',
  inset:     '#EEF1F7',
  bdr:       '#E2E8F0',
  bdr2:      '#CBD5E1',
  green:     '#00B865',
  greenD:    '#006B3F',
  greenBg:   '#E6FAF2',
  greenB:    '#A7F0D2',
  greenGlow: 'rgba(0,184,101,0.22)',
  amber:     '#D97706',
  amberD:    '#92400E',
  amberBg:   '#FFFBEB',
  amberB:    '#FDE68A',
  red:       '#DC2626',
  redD:      '#7F1D1D',
  redBg:     '#FEF2F2',
  redB:      '#FECACA',
  blue:      '#2563EB',
  blueD:     '#1E40AF',
  blueBg:    '#EFF6FF',
  blueB:     '#BFDBFE',
  purple:    '#7C3AED',
  purpD:     '#4C1D95',
  purpBg:    '#F5F3FF',
  purpB:     '#DDD6FE',
  gold:      '#B45309',
  goldBg:    '#FFFBEB',
  goldB:     '#FDE68A',
  text:      '#1A2535',
  text2:     '#4B5E72',
  text3:     '#94A3B8',
};
const FONT_TXT = "'Syne', sans-serif";
const FONT_NUM = "'Outfit', sans-serif";
const SHADOW_SM = '0 1px 3px rgba(15,28,50,0.07), 0 1px 2px rgba(15,28,50,0.04)';

// Paleta de avatar consistente por inicial (mesma das outras páginas)
function avatarPalette(name) {
  const palettes = [
    { bg: '#FEF3C7', color: '#92400E', border: '#FDE68A' }, // amber
    { bg: '#EFF6FF', color: '#1E40AF', border: '#BFDBFE' }, // blue
    { bg: '#F0FDF4', color: '#166534', border: '#A7F0D2' }, // green
    { bg: '#F5F3FF', color: '#4C1D95', border: '#DDD6FE' }, // violet
    { bg: '#FFF7ED', color: '#C2410C', border: '#FED7AA' }, // orange
    { bg: '#FFF1F2', color: '#9F1239', border: '#FECDD3' }, // rose
    { bg: '#ECFDF5', color: '#065F46', border: '#A7F3D0' }, // emerald
    { bg: '#F0F9FF', color: '#0C4A6E', border: '#BAE6FD' }, // sky
  ];
  return palettes[(name || '?').charCodeAt(0) % palettes.length];
}

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now.toLocaleTimeString('pt-BR');
}

// Formata segundos → "Xm Ys" / "Ys" / "—"
function fmtTma(seconds) {
  if (seconds === null || seconds === undefined) return '—';
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
}

export default function AgentsPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const clock = useClock();

  // ─── State ──────────────────────────────────────────────────────────────
  const [agentsBasic, setAgentsBasic] = useState([]);   // /users/agents (fallback)
  const [agentsStats, setAgentsStats] = useState([]);   // /users/agents/stats (preferido)
  const [statsLoaded, setStatsLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all | online | offline
  const [sortBy, setSortBy] = useState('rank');            // rank | name | conversations

  // Modal criar agente
  const [openModal, setOpenModal] = useState(false);
  const [agentName, setAgentName] = useState('');
  const [agentEmail, setAgentEmail] = useState('');
  const [agentPassword, setAgentPassword] = useState('');
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState('');

  // Modal delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // "Definir nova senha" do atendente (out/26). Atendente com e-mail
  // inventado não recebe o código do "Esqueci minha senha"; o supervisor
  // define uma nova aqui e o atendente sai de todos os aparelhos.
  const souSupervisor = (JSON.parse(localStorage.getItem('user') || '{}').role || '').toUpperCase() === 'SUPERVISOR';
  const [senhaDe, setSenhaDe] = useState(null);
  const [novaSenha, setNovaSenha] = useState('');
  const [novaSenha2, setNovaSenha2] = useState('');
  const [salvandoSenha, setSalvandoSenha] = useState(false);
  const [msgSenha, setMsgSenha] = useState({ tipo: '', texto: '' });

  // Limites do plano
  const { usage, refetch: refetchUsage } = usePlanUsage();
  const canAddAgent = !!usage?.agents?.can_add;
  const agentsLimit = usage?.agents?.limit;
  const agentsUsed  = usage?.agents?.used;
  const planName    = usage?.plan;

  // ─── Fetchers ──────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    if (!token) { navigate('/login'); return; }
    try {
      const basic = await api.get('/users/agents', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgentsBasic(basic.data || []);
    } catch (err) {
      console.error('Erro ao carregar agentes:', err);
    }
    try {
      const stats = await api.get('/users/agents/stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgentsStats(stats.data || []);
      setStatsLoaded(true);
    } catch (err) {
      // 403 ou outro erro: fica só com a lista básica
      setAgentsStats([]);
      setStatsLoaded(true);
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchAll();
    // Refresh a cada 30s pra status online/conversas
    const id = setInterval(fetchAll, 30000);
    return () => clearInterval(id);
  }, [fetchAll]);

  // ─── Handlers ──────────────────────────────────────────────────────────
  const handleCreateAgent = async () => {
    if (!agentName || !agentEmail || !agentPassword) {
      setMsg('Preencha todos os campos');
      return;
    }
    if (agentPassword.length < 8) {
      setMsg('A senha precisa ter pelo menos 8 caracteres.');
      return;
    }
    setCreating(true);
    setMsg('');
    try {
      await api.post('/users/agent',
        { name: agentName, email: agentEmail, password: agentPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      refetchUsage();
      fetchAll();
      setMsg('Agente criado com sucesso!');
      setTimeout(() => {
        setOpenModal(false);
        setAgentName(''); setAgentEmail(''); setAgentPassword(''); setMsg('');
      }, 1500);
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Erro ao criar agente');
    } finally {
      setCreating(false);
    }
  };

  const abrirSenha = (ag) => {
    setSenhaDe(ag);
    setNovaSenha(''); setNovaSenha2('');
    setMsgSenha({ tipo: '', texto: '' });
  };

  const handleDefinirSenha = async () => {
    if (novaSenha.length < 8) {
      setMsgSenha({ tipo: 'error', texto: 'A senha precisa ter pelo menos 8 caracteres.' });
      return;
    }
    if (novaSenha !== novaSenha2) {
      setMsgSenha({ tipo: 'error', texto: 'As duas senhas não estão iguais.' });
      return;
    }
    setSalvandoSenha(true);
    try {
      const res = await api.post(`/users/agents/${senhaDe.id}/password`, { nova_senha: novaSenha });
      setMsgSenha({ tipo: 'success', texto: res.data?.message || 'Senha definida.' });
      setNovaSenha(''); setNovaSenha2('');
    } catch (err) {
      const d = err.response?.data?.detail;
      setMsgSenha({ tipo: 'error', texto: typeof d === 'string' ? d : 'Não foi possível definir a senha.' });
    } finally {
      setSalvandoSenha(false);
    }
  };

  const handleDeleteAgent = async (id) => {
    try {
      await api.delete(`/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      refetchUsage();
      fetchAll();
      setDeleteConfirm(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao excluir agente');
    }
  };

  // ─── Lista enriquecida ─────────────────────────────────────────────────
  // Junta o resultado de /agents (lista básica) com /agents/stats (métricas).
  // Se stats não veio (403 ou erro), cai pro básico com métricas zeradas.
  const enrichedAgents = useMemo(() => {
    if (agentsStats.length > 0) return agentsStats;
    return agentsBasic.map((a, i) => ({
      ...a,
      conversations_count: 0,
      templates_sent: 0,
      responses_received: 0,
      conversion_rate: 0,
      avg_response_seconds: null,
      is_online: false,
      rank: i + 1,
    }));
  }, [agentsBasic, agentsStats]);

  const filteredAgents = useMemo(() => {
    let list = enrichedAgents.filter(a =>
      (a.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.email || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filterStatus === 'online')  list = list.filter(a => a.is_online);
    if (filterStatus === 'offline') list = list.filter(a => !a.is_online);
    if (sortBy === 'name')          list = [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    if (sortBy === 'conversations') list = [...list].sort((a, b) => (b.conversations_count || 0) - (a.conversations_count || 0));
    if (sortBy === 'rank')          list = [...list].sort((a, b) => (a.rank || 999) - (b.rank || 999));
    return list;
  }, [enrichedAgents, searchTerm, filterStatus, sortBy]);

  // Counts pros pills do header
  const counts = useMemo(() => ({
    all:     enrichedAgents.length,
    online:  enrichedAgents.filter(a => a.is_online).length,
    offline: enrichedAgents.filter(a => !a.is_online).length,
  }), [enrichedAgents]);

  // ─── Locked slots ───────────────────────────────────────────────────────
  // Mostra "slot bloqueado" apenas quando o plano atual está cheio E NÃO
  // é ENTERPRISE (que tem 9999 = ilimitado). Pra STARTER cheio (1/1)
  // mostra 2 slots placeholder representando upgrade pra BUSINESS.
  // Pra BUSINESS cheio (5/5) mostra 1 slot representando ENTERPRISE.
  // PLUS (out/26) tem 1 agente como o STARTER: mesmo tratamento.
  const umAgente = planName === 'STARTER' || planName === 'PLUS';
  const isAtLimit = usage && agentsUsed >= agentsLimit && planName !== 'ENTERPRISE';
  const lockedSlots = isAtLimit
    ? (umAgente ? 2 : 1)
    : 0;
  const nextPlanName = umAgente ? 'Business' : 'Enterprise';
  const nextPlanDescription = umAgente
    ? 'Disponível no plano Business · até 5 agentes + 1 supervisor'
    : 'Disponível no plano Enterprise · agentes ilimitados';

  // ─── Ranking footer ────────────────────────────────────────────────────
  // STARTER → blur + teaser (não tem dados reais relevantes com 1 agente)
  // BUSINESS/ENTERPRISE → mostra top 3 reais por respostas recebidas
  const showRankingTeaser = umAgente;
  const topThree = useMemo(() =>
    [...enrichedAgents]
      .sort((a, b) => (b.responses_received || 0) - (a.responses_received || 0))
      .slice(0, 3),
    [enrichedAgents]
  );

  // ─── Render helpers ─────────────────────────────────────────────────────
  const RankBadge = ({ rank }) => {
    if (!rank) return <Box sx={badgeSx(C.inset, C.text3, C.bdr, '0.6rem')}>🔒 Bloqueado</Box>;
    if (rank === 1) return <Box sx={badgeSx(C.goldBg, C.gold, C.goldB)}>🥇 1º lugar</Box>;
    if (rank === 2) return <Box sx={badgeSx('#F8FAFC', '#475569', '#CBD5E1')}>🥈 2º lugar</Box>;
    if (rank === 3) return <Box sx={badgeSx('#FFF7ED', '#92400E', '#FED7AA')}>🥉 3º lugar</Box>;
    return <Box sx={badgeSx(C.inset, C.text3, C.bdr, '0.6rem')}>#{rank}</Box>;
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: C.bg, fontFamily: FONT_TXT, color: C.text, overflow: 'hidden' }}>
      <AppSidebar user={user} />

      <Box component="main" sx={{
        flexGrow: 1,
        ml: { xs: 0, md: 'var(--sidebar-width, 260px)' },
        transition: 'margin-left 0.25s ease',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* ═══ TOPBAR LIGHT (inline, diferente do TopBar dark do dashboard) ═══ */}
        <Box sx={{
          height: 54, minHeight: 54,
          bgcolor: C.card, borderBottom: `1px solid ${C.bdr}`,
          display: 'flex', alignItems: 'center', px: 3, gap: 1,
          boxShadow: `0 1px 0 ${C.bdr}`,
        }}>
          <Typography sx={{ fontSize: '0.78rem', color: C.text3, fontFamily: FONT_TXT }}>
            Gerenciamento
          </Typography>
          <Typography sx={{ color: C.bdr2, mx: 0.5 }}>/</Typography>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: C.text, fontFamily: FONT_TXT }}>
            Agentes
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, ml: 'auto' }}>
            <Typography sx={{ fontFamily: FONT_NUM, fontSize: '0.78rem', color: C.text3 }}>
              {clock}
            </Typography>
            <Box
              onClick={() => navigate('/dashboard')}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.4,
                fontSize: '0.78rem', color: C.text3, cursor: 'pointer',
                px: 1.2, py: 0.7, borderRadius: '6px',
                transition: 'all 0.15s', fontFamily: FONT_TXT,
                '&:hover': { color: C.text, bgcolor: C.surf }
              }}
            >
              <FiArrowLeft size={13} /> Voltar
            </Box>
            <Button
              onClick={() => setOpenModal(true)}
              disabled={!canAddAgent}
              title={!canAddAgent ? `Limite do plano ${planName || ''} atingido` : ''}
              sx={{
                bgcolor: canAddAgent ? C.green : C.surf,
                color: canAddAgent ? '#fff' : C.text3,
                border: canAddAgent ? 'none' : `1px solid ${C.bdr}`,
                boxShadow: canAddAgent ? '0 2px 6px rgba(0,184,101,0.22)' : 'none',
                fontFamily: FONT_TXT, fontSize: '0.72rem', fontWeight: 600,
                textTransform: 'none', letterSpacing: '0.02em',
                px: 1.8, py: 0.9, borderRadius: '6px',
                opacity: canAddAgent ? 1 : 0.7,
                cursor: canAddAgent ? 'pointer' : 'not-allowed',
                '&:hover': canAddAgent && { bgcolor: '#00a558' },
                '&.Mui-disabled': {
                  bgcolor: C.surf, color: C.text3,
                  border: `1px solid ${C.bdr}`,
                }
              }}
            >
              <FiPlus size={13} style={{ marginRight: 4 }} />
              Novo Agente
            </Button>
          </Box>
        </Box>

        {/* ═══ CONTENT ═══ */}
        <Box sx={{
          flex: 1, overflowY: 'auto',
          p: { xs: 2.5, md: '24px 28px 32px' },
          display: 'flex', flexDirection: 'column', gap: 2.2,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { background: C.bdr2, borderRadius: 4 },
        }}>

          {/* PAGE HEADER */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2.5, flexWrap: 'wrap' }}>
            {/* Left: title + pills */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.7 }}>
              <Box sx={{
                width: 42, height: 42, borderRadius: '10px',
                bgcolor: C.greenBg, border: `1.5px solid ${C.greenB}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, mt: 0.3,
              }}>
                <FiUsers size={18} color={C.greenD} />
              </Box>
              <Box>
                <Box sx={{
                  fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.18em',
                  textTransform: 'uppercase', color: C.text3,
                  display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.4,
                  '&::before': { content: '""', display: 'inline-block', width: 13, height: 1, bgcolor: C.bdr2 }
                }}>
                  Gerenciamento
                </Box>
                <Typography sx={{
                  fontSize: '1.55rem', fontWeight: 800, color: C.text,
                  letterSpacing: '-0.5px', mb: 0.4, lineHeight: 1.1, fontFamily: FONT_TXT,
                }}>
                  Agentes
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: C.text3, fontFamily: FONT_TXT }}>
                  Gerencie os membros da sua equipe de atendimento.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1.2, flexWrap: 'wrap' }}>
                  <Pill tone="green">
                    {counts.online} agente{counts.online !== 1 ? 's' : ''} ativo{counts.online !== 1 ? 's' : ''}
                  </Pill>
                  {usage && (
                    <Pill tone="blue">
                      Plano {planName} · {agentsUsed} / {agentsLimit === 9999 ? '∞' : agentsLimit} agente{agentsLimit !== 1 ? 's' : ''}
                    </Pill>
                  )}
                  {isAtLimit && (
                    <Pill tone="amber">🔒 Limite atingido</Pill>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Right: search + filters */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                bgcolor: C.card, border: `1px solid ${C.bdr}`,
                borderRadius: '6px', px: 1.6, py: 1,
                width: 260, boxShadow: SHADOW_SM,
                transition: 'all 0.2s',
                '&:focus-within': { borderColor: C.greenB }
              }}>
                <FiSearch size={13} color={C.text3} />
                <Box component="input" type="text"
                  placeholder="Buscar por nome ou email…"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  sx={{
                    flex: 1, background: 'none', border: 'none', outline: 'none',
                    fontFamily: FONT_TXT, fontSize: '0.78rem', color: C.text,
                    '&::placeholder': { color: C.text3 }
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 0.7 }}>
                <FilterBtn active={filterStatus === 'all'}     onClick={() => setFilterStatus('all')}>
                  Todos <FiltCt active={filterStatus === 'all'}>{counts.all}</FiltCt>
                </FilterBtn>
                <FilterBtn active={filterStatus === 'online'}  onClick={() => setFilterStatus('online')}>
                  Online <FiltCt active={filterStatus === 'online'}>{counts.online}</FiltCt>
                </FilterBtn>
                <FilterBtn active={filterStatus === 'offline'} onClick={() => setFilterStatus('offline')}>
                  Offline <FiltCt active={filterStatus === 'offline'}>{counts.offline}</FiltCt>
                </FilterBtn>
                <Box component="select"
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value)}
                  sx={{
                    px: 1.4, py: 0.85, borderRadius: '6px',
                    fontFamily: FONT_TXT, fontSize: '0.72rem', fontWeight: 600,
                    border: `1px solid ${C.bdr}`, bgcolor: C.card, color: C.text2,
                    cursor: 'pointer', outline: 'none',
                  }}
                >
                  <option value="rank">Ordenar: Ranking</option>
                  <option value="name">Ordenar: Nome</option>
                  <option value="conversations">Ordenar: Conversas</option>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* SLIM NOTICE — só aparece se limite atingido */}
          {isAtLimit && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1.2,
              p: '10px 16px',
              bgcolor: C.amberBg, border: `1px solid ${C.amberB}`,
              borderRadius: '6px',
            }}>
              <FiLock size={14} color={C.amberD} />
              <Typography sx={{
                fontSize: '0.8rem', color: C.amberD, flex: 1, lineHeight: 1.5, fontFamily: FONT_TXT,
              }}>
                Limite de agentes atingido no Plano {planName} ({agentsUsed}/{agentsLimit}).
                Para adicionar mais agentes, remova um existente ou faça upgrade do plano.
              </Typography>
              <Box
                onClick={() => navigate('/admin/upgrade')}
                sx={{
                  fontSize: '0.72rem', fontWeight: 700, color: C.amberD,
                  textDecoration: 'underline', cursor: 'pointer',
                  px: 1.2, py: 0.5, borderRadius: '6px',
                  whiteSpace: 'nowrap', fontFamily: FONT_TXT,
                  '&:hover': { bgcolor: 'rgba(217,119,6,0.1)' }
                }}
              >
                Ver planos →
              </Box>
            </Box>
          )}

          {/* SECTION LABEL */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: -1.5 }}>
            <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.text3, fontFamily: FONT_TXT }}>
              Equipe cadastrada
            </Typography>
            <Box sx={{
              fontFamily: FONT_NUM, fontSize: '0.66rem', color: C.text3,
              bgcolor: C.inset, px: 1, py: 0.05, borderRadius: '20px',
              border: `1px solid ${C.bdr}`,
            }}>
              {agentsUsed} de {agentsLimit === 9999 ? '∞' : agentsLimit}
            </Box>
          </Box>

          {/* AGENTS TABLE */}
          <Box sx={{
            bgcolor: C.card, border: `1px solid ${C.bdr}`,
            borderRadius: '10px', overflow: 'hidden', boxShadow: SHADOW_SM,
          }}>
            {/* Head */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: '36px 2.4fr 1.2fr 0.9fr 0.9fr 0.9fr 1.1fr 72px',
              px: 2.5, py: 1.2,
              bgcolor: C.surf, borderBottom: `1px solid ${C.bdr}`,
            }}>
              <Box />
              <Th>Agente</Th>
              <Th>Função</Th>
              <Th>Status</Th>
              <Th sort active={sortBy === 'conversations'} onClick={() => setSortBy('conversations')}>
                Conversas {sortBy === 'conversations' && <span style={{ color: C.greenD, fontSize: '0.7rem' }}>↓</span>}
              </Th>
              <Th>TMA</Th>
              <Th>Ranking</Th>
              <Th sx={{ justifyContent: 'flex-end' }}>Ações</Th>
            </Box>

            {/* Body */}
            {!statsLoaded ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <CircularProgress size={28} sx={{ color: C.green }} />
              </Box>
            ) : filteredAgents.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 5 }}>
                <FiUsers size={28} color={C.text3} />
                <Typography sx={{ color: C.text3, mt: 1, fontSize: '0.85rem', fontFamily: FONT_TXT }}>
                  Nenhum agente {searchTerm ? `para "${searchTerm}"` : 'cadastrado ainda'}.
                </Typography>
              </Box>
            ) : filteredAgents.map((ag) => {
              const avPal = avatarPalette(ag.name);
              const initial = (ag.name || '?')[0]?.toUpperCase();
              return (
                <Box key={ag.id} sx={{
                  display: 'grid',
                  gridTemplateColumns: '36px 2.4fr 1.2fr 0.9fr 0.9fr 0.9fr 1.1fr 72px',
                  px: 2.5, py: 1.6, alignItems: 'center',
                  borderBottom: `1px solid ${C.bdr}`,
                  transition: 'background 0.15s',
                  '&:hover': { bgcolor: C.surf },
                  '&:last-of-type': { borderBottom: 'none' },
                }}>
                  {/* Avatar */}
                  <Box sx={{
                    width: 34, height: 34, borderRadius: '50%',
                    bgcolor: avPal.bg, color: avPal.color,
                    border: `1.5px solid ${avPal.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.85rem', fontWeight: 800, fontFamily: FONT_TXT,
                  }}>
                    {initial}
                  </Box>

                  {/* Name + email */}
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.2 }}>
                      <Typography sx={{
                        fontSize: '0.85rem', fontWeight: 700, color: C.text,
                        fontFamily: FONT_TXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {ag.name}
                      </Typography>
                      <Box sx={{
                        fontSize: '0.6rem', fontWeight: 600, px: 0.7, py: 0.05,
                        borderRadius: '4px', bgcolor: C.greenBg, color: C.greenD,
                        border: `1px solid ${C.greenB}`, fontFamily: FONT_TXT, whiteSpace: 'nowrap',
                      }}>
                        Ativo
                      </Box>
                    </Box>
                    <Typography sx={{
                      fontSize: '0.66rem', color: C.text3, fontFamily: FONT_TXT,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      display: 'flex', alignItems: 'center', gap: 0.4,
                    }}>
                      <FiMail size={10} /> {ag.email}
                    </Typography>
                  </Box>

                  {/* Role */}
                  <Box>
                    <Box sx={badgeSx(
                      ag.role === 'SUPERVISOR' ? C.blueBg : C.greenBg,
                      ag.role === 'SUPERVISOR' ? C.blueD : C.greenD,
                      ag.role === 'SUPERVISOR' ? C.blueB : C.greenB,
                    )}>
                      {ag.role === 'SUPERVISOR' ? 'Supervisor' : 'Agente'}
                    </Box>
                  </Box>

                  {/* Status */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                    <Box sx={{
                      width: 7, height: 7, borderRadius: '50%',
                      bgcolor: ag.is_online ? C.green : C.text3,
                      boxShadow: ag.is_online ? `0 0 0 0 ${C.greenGlow}` : 'none',
                      animation: ag.is_online ? 'agPulse 2.5s infinite' : 'none',
                      '@keyframes agPulse': {
                        '0%, 100%': { boxShadow: `0 0 0 0 ${C.greenGlow}` },
                        '50%':      { boxShadow: '0 0 0 4px rgba(0,184,101,0)' },
                      }
                    }} />
                    <Typography sx={{
                      fontSize: '0.78rem', fontWeight: 500,
                      color: ag.is_online ? C.greenD : C.text3,
                      fontFamily: FONT_TXT,
                    }}>
                      {ag.is_online ? 'Online' : 'Offline'}
                    </Typography>
                  </Box>

                  {/* Conversas */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{
                      fontFamily: FONT_NUM, fontSize: '1rem', fontWeight: 700, lineHeight: 1,
                      color: ag.conversations_count > 0 ? C.greenD : C.text,
                    }}>
                      {ag.conversations_count || 0}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: C.text3, mt: 0.2, fontFamily: FONT_TXT }}>
                      este mês
                    </Typography>
                  </Box>

                  {/* TMA */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.4 }}>
                    <Typography sx={{ fontFamily: FONT_NUM, fontSize: '0.85rem', fontWeight: 700, color: ag.avg_response_seconds ? C.text : C.text3 }}>
                      {fmtTma(ag.avg_response_seconds)}
                    </Typography>
                    {ag.avg_response_seconds && (
                      <Box sx={{ width: 48, height: 3, bgcolor: C.inset, borderRadius: '2px' }}>
                        <Box sx={{
                          height: '100%',
                          width: `${Math.min(100, Math.max(8, 100 - (ag.avg_response_seconds / 600) * 100))}%`,
                          bgcolor: ag.avg_response_seconds < 300 ? C.green : ag.avg_response_seconds < 600 ? C.amber : C.red,
                          borderRadius: '2px',
                          transition: 'width 0.8s ease',
                        }} />
                      </Box>
                    )}
                  </Box>

                  {/* Ranking */}
                  <Box>
                    {/* No plano STARTER mostramos "🔒 Bloqueado" pra incentivar upgrade
                        (ranking só faz sentido com múltiplos agentes). Pra BUSINESS+
                        mostra a posição real do agente. */}
                    {showRankingTeaser ? (
                      <Box sx={badgeSx(C.inset, C.text3, C.bdr, '0.6rem')}>
                        🔒 Bloqueado
                      </Box>
                    ) : (
                      <RankBadge rank={ag.rank} />
                    )}
                  </Box>

                  {/* Ações */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.6 }}>
                    {souSupervisor && ag.role !== 'SUPERVISOR' && (
                      <Box title="Definir nova senha" role="button" tabIndex={0}
                        aria-label={`Definir nova senha de ${ag.name}`}
                        onClick={() => abrirSenha(ag)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); abrirSenha(ag); } }}
                        sx={actBtnSx}>
                        <FiKey size={12} />
                      </Box>
                    )}
                    <Box title="Editar"
                      sx={actBtnSx}>
                      <FiEdit2 size={12} />
                    </Box>
                    <Box title="Excluir"
                      onClick={() => setDeleteConfirm(ag)}
                      sx={{
                        ...actBtnSx,
                        '&:hover': { bgcolor: C.redBg, color: C.redD, borderColor: C.redB },
                      }}>
                      <FiTrash2 size={12} />
                    </Box>
                  </Box>
                </Box>
              );
            })}

            {/* LOCKED SLOT ROWS — placeholders de upgrade */}
            {Array.from({ length: lockedSlots }).map((_, i) => (
              <Box key={`locked-${i}`} sx={{
                display: 'flex', alignItems: 'center', gap: 1.7,
                p: '11px 20px',
                borderBottom: i < lockedSlots - 1 ? `1px solid ${C.bdr}` : 'none',
                bgcolor: C.surf,
              }}>
                <Box sx={{
                  width: 34, height: 34, borderRadius: '50%',
                  bgcolor: C.inset, border: `1.5px dashed ${C.bdr2}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: C.text3, fontSize: '0.95rem', flexShrink: 0,
                }}>
                  +
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: C.text2, fontFamily: FONT_TXT }}>
                    Slot de agente bloqueado
                  </Typography>
                  <Typography sx={{ fontSize: '0.66rem', color: C.text3, mt: 0.1, fontFamily: FONT_TXT }}>
                    {nextPlanDescription}
                  </Typography>
                </Box>
                <Box
                  onClick={() => navigate('/admin/upgrade')}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.4,
                    fontSize: '0.66rem', fontWeight: 700, color: C.amberD,
                    bgcolor: C.amberBg, border: `1px solid ${C.amberB}`,
                    px: 1.3, py: 0.55, borderRadius: '6px',
                    cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: FONT_TXT,
                    '&:hover': { bgcolor: '#FEF3C7' },
                  }}>
                  <FiZap size={11} /> Desbloquear
                </Box>
              </Box>
            ))}
          </Box>

          {/* RANKING FOOTER — teaser pro STARTER, real pra BUSINESS+ */}
          <Box sx={{
            bgcolor: C.card, border: `1px solid ${C.bdr}`,
            borderRadius: '10px', overflow: 'hidden', boxShadow: SHADOW_SM,
          }}>
            <Box sx={{
              p: '12px 20px', borderBottom: `1px solid ${C.bdr}`,
              display: 'flex', alignItems: 'center', gap: 1, bgcolor: C.surf,
            }}>
              <FiStar size={14} color={C.amber} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: C.text, flex: 1, fontFamily: FONT_TXT }}>
                {showRankingTeaser
                  ? `Ranking de performance — disponível no plano ${nextPlanName}`
                  : 'Top 3 agentes — este mês'}
              </Typography>
              {showRankingTeaser && (
                <Box
                  onClick={() => navigate('/admin/upgrade')}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.4,
                    fontSize: '0.66rem', fontWeight: 700, color: C.purple,
                    bgcolor: C.purpBg, border: `1px solid ${C.purpB}`,
                    px: 1.3, py: 0.5, borderRadius: '6px',
                    cursor: 'pointer', fontFamily: FONT_TXT,
                    '&:hover': { bgcolor: '#EDE9FE' },
                  }}>
                  <FiZap size={11} /> Desbloquear
                </Box>
              )}
            </Box>

            <Box sx={{
              p: '14px 20px',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
              gap: 1.2,
              // teaser: blurra + desabilita interação
              filter: showRankingTeaser ? 'blur(3.5px)' : 'none',
              pointerEvents: showRankingTeaser ? 'none' : 'auto',
              userSelect: showRankingTeaser ? 'none' : 'auto',
            }}>
              {(showRankingTeaser ? PLACEHOLDER_TOP_3 : topThree).map((p, i) => {
                const medals = ['🥇', '🥈', '🥉'];
                const colors = [C.gold, '#475569', '#92400E'];
                const score = showRankingTeaser ? p.score : (p.responses_received || 0);
                return (
                  <Box key={p.id || i} sx={{
                    display: 'flex', alignItems: 'center', gap: 1.2,
                    p: '10px 14px', borderRadius: '6px',
                    border: `1px solid ${C.bdr}`, bgcolor: C.surf,
                  }}>
                    <Typography sx={{ fontSize: '1.25rem' }}>{medals[i]}</Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{
                        fontSize: '0.78rem', fontWeight: 600, color: C.text, fontFamily: FONT_TXT,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {p.name || '—'}
                      </Typography>
                      <Typography sx={{ fontFamily: FONT_NUM, fontSize: '1.1rem', fontWeight: 700, color: colors[i], lineHeight: 1 }}>
                        {score}
                      </Typography>
                      <Typography sx={{ fontSize: '0.6rem', color: C.text3, mt: 0.1, fontFamily: FONT_TXT }}>
                        {showRankingTeaser ? 'conversas / mês' : 'respostas recebidas'}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {showRankingTeaser && (
              <Typography sx={{
                textAlign: 'center', p: '8px 20px 14px',
                fontSize: '0.72rem', color: C.text3, fontFamily: FONT_TXT,
              }}>
                Faça upgrade para ver o ranking completo e métricas individuais de cada agente.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* MODAL: Criar Agente */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '10px' } }}>
        <DialogTitle sx={{ bgcolor: C.text, color: '#fff', fontWeight: 800, py: 2.5, px: 3, fontFamily: FONT_TXT }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <FiPlus size={18} color={C.green} />
            Novo Agente
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, px: 3 }}>
          <TextField label="Nome completo" fullWidth value={agentName}
            onChange={e => setAgentName(e.target.value)} sx={{ mb: 2.5, mt: 1 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><FiUser size={14} color={C.text3} /></InputAdornment> }} />
          <TextField label="E-mail" type="email" fullWidth value={agentEmail}
            onChange={e => setAgentEmail(e.target.value)} sx={{ mb: 2.5 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><FiMail size={14} color={C.text3} /></InputAdornment> }} />
          <TextField label="Senha" type="password" fullWidth value={agentPassword}
            onChange={e => setAgentPassword(e.target.value)} sx={{ mb: 1.5 }}
            helperText="Mínimo 8 caracteres"
            InputProps={{ startAdornment: <InputAdornment position="start"><FiLock size={14} color={C.text3} /></InputAdornment> }} />
          {msg && <Alert severity={msg.includes('sucesso') ? 'success' : 'error'} sx={{ mt: 1.5, borderRadius: '6px' }}>{msg}</Alert>}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, px: 3, gap: 1 }}>
          <Button onClick={() => setOpenModal(false)} sx={{ textTransform: 'none', color: C.text2, fontFamily: FONT_TXT }}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleCreateAgent} disabled={creating}
            sx={{ bgcolor: C.green, textTransform: 'none', fontWeight: 700, px: 3, borderRadius: '6px', fontFamily: FONT_TXT,
                  '&:hover': { bgcolor: '#00a558' } }}>
            {creating ? 'Criando...' : 'Criar Agente'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL: Definir nova senha do atendente */}
      <Dialog open={!!senhaDe} onClose={() => !salvandoSenha && setSenhaDe(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: '10px' } }}>
        <DialogTitle sx={{ fontWeight: 800, color: C.text, fontFamily: FONT_TXT }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <FiKey size={18} color={C.green} />
            Nova senha de {senhaDe?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: C.text2, fontFamily: FONT_TXT, fontSize: '0.82rem', mb: 2 }}>
            Ao salvar, {senhaDe?.name} sai de todos os aparelhos e entra de novo com esta senha.
            Combine com a pessoa como a senha vai chegar até ela.
          </Typography>
          {msgSenha.tipo !== 'success' && <>
          <TextField label="Senha nova" type="password" fullWidth value={novaSenha} autoComplete="new-password"
            onChange={e => setNovaSenha(e.target.value)} sx={{ mb: 2, mt: 0.5 }}
            helperText="Mínimo 8 caracteres"
            InputProps={{ startAdornment: <InputAdornment position="start"><FiLock size={14} color={C.text3} /></InputAdornment> }} />
          <TextField label="Repita a senha nova" type="password" fullWidth value={novaSenha2} autoComplete="new-password"
            onChange={e => setNovaSenha2(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><FiLock size={14} color={C.text3} /></InputAdornment> }} />
          </>}
          {msgSenha.texto && (
            <Alert severity={msgSenha.tipo || 'info'} sx={{ mt: 2, borderRadius: '6px' }}>{msgSenha.texto}</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={() => setSenhaDe(null)} disabled={salvandoSenha}
            sx={{ textTransform: 'none', color: C.text2, fontFamily: FONT_TXT }}>
            {msgSenha.tipo === 'success' ? 'Fechar' : 'Cancelar'}
          </Button>
          {msgSenha.tipo !== 'success' && (
            <Button variant="contained" onClick={handleDefinirSenha} disabled={salvandoSenha}
              sx={{ bgcolor: C.green, textTransform: 'none', fontWeight: 700, px: 3, borderRadius: '6px', fontFamily: FONT_TXT,
                    '&:hover': { bgcolor: '#00a558' } }}>
              {salvandoSenha ? 'Salvando...' : 'Definir senha'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* MODAL: Confirmar exclusão */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}
        PaperProps={{ sx: { borderRadius: '10px' } }}>
        <DialogTitle sx={{ fontWeight: 800, color: C.text, fontFamily: FONT_TXT }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <FiTrash2 size={18} color={C.red} />
            Excluir agente?
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: C.text2, fontFamily: FONT_TXT, fontSize: '0.85rem' }}>
            Tem certeza que deseja excluir <strong>{deleteConfirm?.name}</strong>?
            Esta ação não pode ser desfeita; o agente perderá acesso ao sistema.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={() => setDeleteConfirm(null)} sx={{ textTransform: 'none', color: C.text2, fontFamily: FONT_TXT }}>
            Cancelar
          </Button>
          <Button color="error" variant="contained"
            onClick={() => handleDeleteAgent(deleteConfirm.id)}
            startIcon={<FiTrash2 size={13} />}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '6px', fontFamily: FONT_TXT }}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Sub-componentes auxiliares ──────────────────────────────────────────────

function Pill({ tone, children }) {
  const map = {
    green: { bg: C.greenBg, color: C.greenD, border: C.greenB },
    blue:  { bg: C.blueBg,  color: C.blueD,  border: C.blueB  },
    amber: { bg: C.amberBg, color: C.amberD, border: C.amberB },
  };
  const p = map[tone] || map.green;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 1.4, py: 0.4, borderRadius: '20px',
      fontSize: '0.66rem', fontWeight: 600,
      bgcolor: p.bg, color: p.color, border: `1px solid ${p.border}`,
      fontFamily: FONT_TXT,
    }}>
      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: 'currentColor' }} />
      {children}
    </Box>
  );
}

function Th({ children, sort, active, onClick, sx }) {
  return (
    <Box onClick={onClick}
      sx={{
        fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: active ? C.text2 : C.text3,
        display: 'flex', alignItems: 'center', gap: 0.3,
        cursor: sort ? 'pointer' : 'default',
        fontFamily: FONT_TXT,
        '&:hover': sort && { color: C.text2 },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function FilterBtn({ active, onClick, children }) {
  return (
    <Box onClick={onClick}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.3,
        px: 1.3, py: 0.7, borderRadius: '6px',
        fontFamily: FONT_TXT, fontSize: '0.72rem', fontWeight: 600,
        cursor: 'pointer',
        border: `1px solid ${active ? C.greenB : C.bdr}`,
        bgcolor: active ? C.greenBg : C.card,
        color: active ? C.greenD : C.text2,
        transition: 'all 0.15s',
        '&:hover': !active && { borderColor: C.bdr2, color: C.text }
      }}>
      {children}
    </Box>
  );
}

function FiltCt({ active, children }) {
  return (
    <Box sx={{
      fontFamily: FONT_NUM, fontSize: '0.6rem',
      bgcolor: active ? 'rgba(255,255,255,0.4)' : C.inset,
      px: 0.5, borderRadius: '4px', ml: 0.2,
    }}>
      {children}
    </Box>
  );
}

const badgeSx = (bg, color, border, fontSize = '0.66rem') => ({
  display: 'inline-flex', alignItems: 'center', gap: 0.4,
  px: 1.2, py: 0.4, borderRadius: '20px',
  fontSize, fontWeight: 600,
  bgcolor: bg, color, border: `1px solid ${border}`,
  fontFamily: FONT_TXT, whiteSpace: 'nowrap',
});

const actBtnSx = {
  width: 26, height: 26, borderRadius: '6px',
  border: `1px solid ${C.bdr}`, bgcolor: C.card,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', color: C.text3,
  transition: 'all 0.15s',
  '&:hover': { borderColor: C.bdr2, color: C.text },
};

// Placeholder do ranking footer pra teaser do STARTER. Nomes inventados
// pra dar a "sensação" de como ficaria com dados reais.
const PLACEHOLDER_TOP_3 = [
  { id: 'p1', name: 'Lucas Pereira',  score: 248 },
  { id: 'p2', name: 'Bruno Ignácio',  score: 187 },
  { id: 'p3', name: 'Rafael Fontes',  score: 151 },
];
